import { useEffect } from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { MytaverseLogger } from '@helpers/logger';
import {
  LastWebsocketJsonMessageType,
  WebsocketAction,
} from '@interfaces/webSocketConnectionInfo';

/**
 * The room's live branding changed — possibly by somebody else.
 *
 * `activeBrandingId` used to be written only by this client's own actions, so a
 * branding activated by another participant never reached the branding list:
 * the list is a one-shot fetch keyed on `currentRoom.activeBrandingId`, and
 * that value stayed stale until the modal was reopened. The backend now
 * broadcasts `BRANDING_ACTIVATED` to every hub in the room; putting the id into
 * `currentRoom` is enough on its own, since both the list
 * (`AdminTools` reloads on that dependency) and the editor's quick switcher
 * (which renders its Live badge straight from it) already follow it.
 *
 * `currentBrandingId` is deliberately left alone. It means "the configuration
 * this client has open", is written only by the branding UI, and — unlike
 * `activeBrandingId` — nothing resets it when the participant changes rooms.
 * Setting it here would pin every bystander in the room to that id and carry it
 * into the next room, where `currentBrandingId ?? currentRoom.activeBrandingId`
 * would then resolve branding spots, hotspots and map-editor layouts against
 * another map's configuration. Anyone who needs it in sync gets it from
 * `AdminTools`, which reloads off the same `activeBrandingId` this hook writes.
 *
 * @param lastJsonMessage - Latest message off the dashboard websocket. Anything
 * other than `BRANDING_ACTIVATED`, or an activation in a room this participant
 * is not in, is ignored.
 */
export const useHandleBrandingActivatedMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const { currentRoom, setCurrentRoom } = useMytaverseEvent();

  const currentRoomId = currentRoom?.roomId;

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    const { action, roomId, brandingId } = lastJsonMessage;

    if (action !== WebsocketAction.BrandingActivated) {
      return;
    }

    if (!brandingId || !roomId || roomId !== currentRoomId) {
      return;
    }

    MytaverseLogger.log(
      `Room ${roomId} switched its active branding to ${brandingId}`,
    );

    // Returning `prev` unchanged is what keeps this cheap. `currentRoom` is an
    // object-identity dependency of the branding-spot, hotspot and point-of-
    // interest effects, and the last of those blanks its markers before
    // refetching — so a new object for an id the room already holds would make
    // every activation flicker for everyone, the activating client included,
    // since it receives its own broadcast back.
    setCurrentRoom((prev) => {
      if (!prev || prev.roomId !== roomId) {
        return prev;
      }

      return prev.activeBrandingId === brandingId
        ? prev
        : { ...prev, activeBrandingId: brandingId };
    });
  }, [lastJsonMessage, currentRoomId, setCurrentRoom]);
};
